// Mock data and helpers for API mock mode
// Used for testing UI flows without backend dependency

import type { ApiResponse, Dealer, LoginResponse } from '@/types';
import { DealerRegion, DealerType } from '@/types';

// ============================================================================
// Mock Data
// ============================================================================

const MOCK_TOKEN = 'mock-jwt-token-for-testing-purposes-only';

type MockDealer = Dealer & { password: string };

const MOCK_DEALERS: Record<string, MockDealer> = {
  'john@baywaylincoln.test': {
    dealer_id: 90001,
    dealer_name: 'Bayway Lincoln',
    dealer_type: DealerType.L,
    parts_code: '67032',
    address: '12345 Gulf Freeway',
    city: 'Houston',
    state: 'TX',
    zip: '77034',
    phone: '713-555-0100',
    dealer_region: DealerRegion.CE,
    primary_contact: 'John Smith',
    contact_email: 'john@baywaylincoln.test',
    contact_phone: '713-555-0101',
    active: true,
    attended_training: true,
    training_attendee_name: 'John Smith',
    training_date: null as unknown as Date,
    password: 'bayway123',
  },
  'sarah@mcdavidlincoln.test': {
    dealer_id: 90002,
    dealer_name: 'David McDavid Lincoln',
    dealer_type: DealerType.L,
    parts_code: '67324',
    address: '5000 N Central Expy',
    city: 'Dallas',
    state: 'TX',
    zip: '75206',
    phone: '214-555-0200',
    dealer_region: DealerRegion.CE,
    primary_contact: 'Sarah Johnson',
    contact_email: 'sarah@mcdavidlincoln.test',
    contact_phone: '214-555-0201',
    active: true,
    attended_training: false,
    training_attendee_name: '',
    training_date: null as unknown as Date,
    password: 'mcdavid123',
  },
};

// ============================================================================
// Response Helpers
// ============================================================================

/**
 * Creates a mock Response object for the ApiResponse pattern.
 */
function createMockResponse(ok: boolean, status: number): Response {
  return {
    ok,
    status,
    statusText: ok ? 'OK' : 'Unauthorized',
    headers: new Headers(),
    redirected: false,
    type: 'basic',
    url: '',
    clone: () => createMockResponse(ok, status),
    body: null,
    bodyUsed: false,
    arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
    blob: () => Promise.resolve(new Blob()),
    formData: () => Promise.resolve(new FormData()),
    json: () => Promise.resolve({}),
    text: () => Promise.resolve(''),
  } as Response;
}

// ============================================================================
// Mock API Functions
// ============================================================================

/**
 * Mock login - validates credentials against MOCK_DEALERS.
 * Returns success with token and dealer data, or 401 for invalid credentials.
 */
export function mockLogin(email: string, password: string): ApiResponse<LoginResponse> {
  const normalizedEmail = email.toLowerCase();
  const mockDealer = MOCK_DEALERS[normalizedEmail];

  if (mockDealer && mockDealer.password === password) {
    // Remove password from the dealer object before returning
    const dealer: Dealer = {
      dealer_id: mockDealer.dealer_id,
      dealer_name: mockDealer.dealer_name,
      dealer_type: mockDealer.dealer_type,
      parts_code: mockDealer.parts_code,
      address: mockDealer.address,
      address2: mockDealer.address2,
      city: mockDealer.city,
      state: mockDealer.state,
      zip: mockDealer.zip,
      phone: mockDealer.phone,
      dealer_region: mockDealer.dealer_region,
      primary_contact: mockDealer.primary_contact,
      contact_email: mockDealer.contact_email,
      contact_phone: mockDealer.contact_phone,
      active: mockDealer.active,
      attended_training: mockDealer.attended_training,
      training_attendee_name: mockDealer.training_attendee_name,
      training_date: mockDealer.training_date,
    };
    return {
      response: createMockResponse(true, 200),
      result: {
        token: MOCK_TOKEN,
        dealer,
      },
    };
  }

  // Invalid credentials
  return {
    response: createMockResponse(false, 401),
    result: undefined,
  };
}

/**
 * Mock signup - validates for duplicate email/parts_code before creating.
 * Merges submitted form data with base dealer template.
 */
export function mockSignup(dealer: Partial<Dealer>): ApiResponse<LoginResponse> {
  // Check for duplicate email
  const normalizedEmail = dealer.contact_email?.toLowerCase();
  if (normalizedEmail && MOCK_DEALERS[normalizedEmail]) {
    return {
      response: createMockResponse(false, 409),
      result: {
        errors: ['An account with this email already exists.'],
      } as unknown as LoginResponse,
    };
  }

  // Check for duplicate parts_code
  if (dealer.parts_code) {
    const existingDealer = Object.values(MOCK_DEALERS).find(
      (d) => d.parts_code === dealer.parts_code
    );
    if (existingDealer) {
      return {
        response: createMockResponse(false, 409),
        result: {
          errors: ['A dealer with this sales code is already registered.'],
        } as unknown as LoginResponse,
      };
    }
  }

  // Use first mock dealer as template (we know it exists)
  const template = MOCK_DEALERS['john@baywaylincoln.test']!;

  const newDealer: Dealer = {
    dealer_id: Date.now(), // Generate unique ID
    dealer_name: dealer.dealer_name ?? template.dealer_name,
    dealer_type: dealer.dealer_type ?? template.dealer_type,
    parts_code: dealer.parts_code ?? template.parts_code,
    address: dealer.address ?? template.address,
    address2: dealer.address2,
    city: dealer.city ?? template.city,
    state: dealer.state ?? template.state,
    zip: dealer.zip ?? template.zip,
    phone: dealer.phone ?? template.phone,
    dealer_region: dealer.dealer_region ?? template.dealer_region,
    primary_contact: dealer.primary_contact ?? template.primary_contact,
    contact_email: dealer.contact_email ?? template.contact_email,
    contact_phone: dealer.contact_phone ?? template.contact_phone,
    active: true,
    attended_training: false,
    training_attendee_name: '',
    training_date: null as unknown as Date,
  };

  return {
    response: createMockResponse(true, 200),
    result: {
      token: MOCK_TOKEN,
      dealer: newDealer,
    },
  };
}
